import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Check, Loader2 } from 'lucide-react';

type Status = 'idle' | 'loading' | 'success' | 'duplicate' | 'error';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WaitlistDialog({ open, onOpenChange }: Props) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setEmail('');
        setStatus('idle');
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) return;

    setStatus('loading');

    // Through the rate-limited function (supabase/functions/waitlist-signup),
    // never straight into the table: the direct insert had no limit of its
    // own and a script could fill the list (2026-11-01). The function answers
    // { duplicate: true } when the email is already on it.
    const { data, error } = await supabase.functions.invoke<{ ok: boolean; duplicate: boolean }>('waitlist-signup', {
      body: { email: trimmed, source: typeof window !== 'undefined' ? window.location.hostname : null },
    });

    if (error || !data?.ok) {
      setStatus('error');
      return;
    }
    setStatus(data.duplicate ? 'duplicate' : 'success');
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Join the waitlist</DialogTitle>
          <DialogDescription>
            {status === 'success'
              ? "You're on the list. We'll email you as soon as Contruly opens for early access."
              : "We'll email you the moment Contruly opens for early access. No spam, ever."}
          </DialogDescription>
        </DialogHeader>

        {status === 'success' ? (
          <div className="flex flex-col items-center py-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Check className="h-6 w-6 text-primary" aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground">Thanks, {email}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-2">
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === 'loading'}
              required
              autoFocus
              aria-label="Email address"
            />
            {status === 'duplicate' && (
              <p className="text-sm text-muted-foreground">
                You're already on the list. Thanks for your patience.
              </p>
            )}
            {status === 'error' && (
              <p className="text-sm text-destructive">
                Something went wrong. Please try again in a moment.
              </p>
            )}
            <Button type="submit" disabled={status === 'loading'} className="w-full">
              {status === 'loading' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Signing up…
                </>
              ) : (
                'Join waitlist'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
